import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Zap, CheckCircle, Radar, Search, Shield } from 'lucide-react';
import { WatcherIcon } from './CustomIcons';
import { Button } from './ui/button';
import { InteractiveCard } from './InteractiveCard';
import Footer from './Footer';

export default function CyberSecurityPage() {
  useEffect(() => {
    document.title = 'Cyber Security Services - The Watcher | Semando';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Advanced cyber security and threat detection by The Watcher. 24/7 monitoring, penetration testing, and incident response. $550/day for omnipresent protection.');
    }
  }, []);
  const offerings = [
    {
      icon: Radar,
      title: 'Threat Detection',
      description: 'Advanced monitoring systems that identify and respond to security threats in real-time.'
    },
    {
      icon: Search,
      title: 'Penetration Testing',
      description: 'Comprehensive security assessments that identify vulnerabilities before attackers do.'
    },
    {
      icon: Shield,
      title: 'Security Architecture',
      description: 'Design and implementation of robust security frameworks tailored to your infrastructure.'
    },
    {
      icon: AlertTriangle,
      title: 'Incident Response',
      description: 'Rapid response protocols and forensic analysis to minimize damage from security breaches.'
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Omnipresent Protection',
      description: 'Continuous surveillance and predictive analytics that anticipate threats before they materialize.'
    },
    {
      icon: Zap,
      title: 'Instant Response',
      description: 'Lightning-fast threat neutralization with automated containment protocols that never sleep.'
    }
  ];

  return (
    <div className="min-h-screen space-y-0">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-background to-muted/20 relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                <WatcherIcon className="h-5 w-5 text-primary mr-2" size={20} />
                <span className="text-sm text-primary">THREAT_SCAN_ACTIVE</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">
                <span className="block text-muted-foreground text-xl md:text-2xl mb-4">THE WATCHER</span>
                The only way to beat a hacker <br /> is to think like one. 
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                In the shadows of cyberspace, threats evolve constantly. We stand guard—monitoring, analyzing, protecting. While you build your empire, we watch for those who would tear it down.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl">Eyes Everywhere</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Multi-layered surveillance and protection systems that see threats from every angle, every moment.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {offerings.map((offering, index) => (
                <InteractiveCard key={index} className="hover-neon">
                  <div className="p-8 space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg">
                      <offering.icon className="h-8 w-8 text-primary" size={32} />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl">{offering.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{offering.description}</p>
                    </div>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 bg-gradient-to-r from-muted/10 via-transparent to-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl">Why The Watcher Never Rests</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Cyber threats don't keep business hours. Neither do we. In the eternal game of digital warfare, constant vigilance is the price of security.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Perpetual Surveillance</h3>
                  <p className="text-muted-foreground">
                    Every packet, every login, every anomaly monitored in real-time. Nothing escapes our observation networks.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Predictive Intelligence</h3>
                  <p className="text-muted-foreground">
                    We don't just react to threats—we anticipate them. Advanced analytics predict attack patterns before they unfold.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Guardian Protocol</h3>
                  <p className="text-muted-foreground">
                    When threats emerge, we respond instantly. Automated containment, forensic analysis, and rapid neutralization.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid gap-6">
              {benefits.map((benefit, index) => (
                <InteractiveCard key={index}>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                        <benefit.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-lg">{benefit.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-16">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl">Under The Watcher's Protection</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                When The Watcher stands guard, your digital assets exist in a sphere of absolute security and constant vigilance.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Radar className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Total Awareness</h3>
                <p className="text-muted-foreground">Comprehensive monitoring networks that track every digital footprint and potential threat vector.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Instant Neutralization</h3>
                <p className="text-muted-foreground">Threats eliminated within milliseconds of detection through automated response systems.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Impenetrable Defense</h3>
                <p className="text-muted-foreground">Multi-layered security architecture that adapts and strengthens against evolving threats.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & CTA */}
      <section className="py-24 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8 relative z-10">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl">Hire The Watcher</h2>
            <p className="text-xl text-muted-foreground">
              Daily rate for senior cybersecurity watcher.
            </p>
            <div className="text-6xl text-primary">$550<span className="text-2xl text-muted-foreground">/day</span></div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Security isn't a product—it's a commitment to eternal vigilance in an ever-changing threat landscape.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="group bg-primary text-primary-foreground hover:bg-primary/90 cyber-glow">
              <Link to="/contact">
                Activate Protection
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}